import {
  EvidenceDefinition,
  IEvidence,
  LearningObjective,
  ProgramData,
  ProgramSkill,
  SkillsMultiSelectSelectedValue,
  WPBAForm,
} from "@interfaces/Interfaces";
import { addDays, isAfter, isBefore } from "date-fns";

export function createSkillsEvidenceTitle(params: {
  evidence: IEvidence;
  preselectedSkill: ProgramSkill | null;
  draft: boolean;
  draftsLength: number;
}) {
  const { evidence, preselectedSkill, draft, draftsLength } = params;

  const skillsSelect = evidence.evidenceJSON.SkillsSelect;

  if (!skillsSelect) {
    return;
  }

  const mappedSkills = evidence.evidenceJSON.SkillsSelect.map(
    (skill: SkillsMultiSelectSelectedValue) => skill.skillName
  ).sort();

  const preselectedSkillName = preselectedSkill?.Name;

  const skillsCount = mappedSkills.length;

  const originalTitle = evidence.evidenceJSON.Title;
  let newTitle;

  if (originalTitle) {
    const title = originalTitle;
    const titleSplit = title.split(" +");

    if (skillsCount === 1) {
      if (titleSplit[0] !== mappedSkills[0]) {
        newTitle = mappedSkills[0];
      }
    } else if (skillsCount > 1) {
      if (titleSplit[0] !== mappedSkills[0]) {
        newTitle = `${mappedSkills[0]} +${skillsCount - 1}`;
      } else {
        newTitle = `${titleSplit[0]} +${skillsCount - 1}`;
      }
    } else if (skillsCount === 0 && draft) {
      newTitle = `My Draft #${draftsLength ? draftsLength + 1 : "1"}`;
    }
  } else {
    if (skillsCount > 1) {
      if (preselectedSkillName) {
        if (mappedSkills.includes(preselectedSkillName)) {
          newTitle = `${preselectedSkillName} +${skillsCount - 1}`;
        } else {
          newTitle = `${mappedSkills[0]} +${skillsCount - 1}`;
        }
      } else {
        newTitle = `${mappedSkills[0]} +${skillsCount - 1}`;
      }
    } else if (skillsCount === 1) {
      if (preselectedSkillName) {
        if (mappedSkills.includes(preselectedSkillName)) {
          newTitle = preselectedSkillName;
        } else {
          newTitle = mappedSkills[0];
        }
      } else {
        newTitle = mappedSkills[0];
      }
    }
  }

  return newTitle;
}

export function createWPBAFormEvidenceTitle(params: { form: WPBAForm | null; draft: boolean; draftsLength: number }) {
  const { form, draft, draftsLength } = params;

  const formName = form?.Name;

  if (draft) {
    return `My Draft #${draftsLength ? draftsLength + 1 : "1"}`;
  }

  return formName ?? "Untitled";
}

export function isSkillEvidence(params: { evidence: IEvidence; program: ProgramData }): boolean {
  const { evidence, program } = params;

  if (evidence.evidenceJSON?.SkillsSelect) {
    return true;
  }

  const evidenceDefinitions = program["Evidence Definitions"] ?? [];
  const evidenceDefinition = evidenceDefinitions.find(
    (item: EvidenceDefinition) => item.EvidenceSelectionType === "skills"
  )!;

  const evidenceDefinitionID = evidence.evidenceJSON?.programInfo?.programEvidenceDefinitionID;

  return evidenceDefinitionID === evidenceDefinition.ID;
}

export function sortLOsUsingDisplayName(a: LearningObjective, b: LearningObjective): number {
  if (a["Display Name"] == b["Display Name"]) {
    return 0;
  }
  const splitAName: string[] = a["Display Name"]?.split(".") ?? [];
  const splitBName: string[] = b["Display Name"]?.split(".") ?? [];
  for (let i = 0; i < 5; i++) {
    let compareValue: number;
    const aNum: number = parseInt(splitAName[i], 10);
    const bNum: number = parseInt(splitBName[i], 10);
    if (Number.isNaN(aNum) || Number.isNaN(bNum)) {
      compareValue = splitAName[i].localeCompare(splitBName[i]);
    } else {
      compareValue = aNum - bNum;
    }
    if (compareValue != 0) {
      return compareValue;
    }
  }
  return 0;
}

export function sortLOsUsingName(a: LearningObjective, b: LearningObjective): number {
  if (a.Name == b.Name) {
    return 0;
  }
  const splitAName: string[] = a.Name.split(".") ?? [];
  const splitBName: string[] = b.Name.split(".") ?? [];
  for (let i = 0; i < 5; i++) {
    let compareValue: number;
    const aNum: number = parseInt(splitAName[i], 10);
    const bNum: number = parseInt(splitBName[i], 10);
    if (Number.isNaN(aNum) || Number.isNaN(bNum)) {
      compareValue = splitAName[i].localeCompare(splitBName[i]);
    } else {
      compareValue = aNum - bNum;
    }
    if (compareValue != 0) {
      return compareValue;
    }
  }
  return 0;
}

export function isWPBAFormEvidencePending(evidence: IEvidence): boolean {
  let pending = false;

  if (evidence.evidenceJSON.programInfo.locked) {
    return pending;
  }

  if (evidence.evidenceJSON.programInfo.pending && evidence.createdAt) {
    pending = isBefore(new Date(), addDays(new Date(evidence.createdAt), 7));
  }

  if (evidence.evidenceJSON.programInfo.expiryDate) {
    pending = !isAfter(new Date(), new Date(evidence.evidenceJSON.programInfo.expiryDate));
  }

  return pending;
}

export function isWPBAFormEvidenceExpired(evidence: IEvidence): boolean {
  let expired = false;

  if (evidence.evidenceJSON.programInfo.locked) {
    return expired;
  }

  if (evidence.evidenceJSON.programInfo.pending && evidence.createdAt) {
    expired = isAfter(new Date(), addDays(new Date(evidence.createdAt), 7));
  }

  if (evidence.evidenceJSON.programInfo.expiryDate) {
    expired = isAfter(new Date(), new Date(evidence.evidenceJSON.programInfo.expiryDate));
  }

  return expired;
}
